/**
 * Internationalisation du Sound Catalog.
 * Détecte la langue du navigateur et retourne les chaînes UI correspondantes.
 * Les données musicales (titres, projets, styles) ne sont jamais traduites.
 */

const translations = {
  fr: {
    // Recherche & filtres
    searchPlaceholder: 'Rechercher un morceau…',
    allProjects: 'Tous les projets',
    allStyles: 'Tous les styles',
    mainProjects: 'Projets Principaux',
    backCatalog: 'Catalogue',
    filterByProject: 'Filtrer par projet',
    filterByStyle: 'Filtrer par style',
    results: (n) => `${n} résultat${n > 1 ? 's' : ''}`,
    noResults: 'Aucun morceau trouvé',
    // Lecteur
    prev: 'Précédent',
    next: 'Suivant',
    pause: 'Pause',
    play: 'Lecture',
    volume: 'Volume',
    volumeControl: 'Réglage du volume',
    // Sélection / panier
    mySelection: 'Ma sélection',
    likedTracksDialog: 'Titres likés',
    emptyCart: 'Likez des titres avec le cœur pour les ajouter ici.',
    downloading: 'Téléchargement…',
    download: 'Télécharger',
    downloadCount: (n) => `Télécharger (${n})`,
    downloadError: 'Erreur lors du téléchargement.',
    addToSelection: (name) => `Ajouter ${name} à ma sélection`,
    removeFromSelection: (name) => `Retirer ${name} de ma sélection`,
    removeTrack: (name) => `Retirer ${name}`,
    // Thème & UI
    palette: 'Palette',
    colorPalettes: 'Palettes de couleurs',
    choosePalette: 'Choisir une palette de couleurs',
    close: 'Fermer',
    // Info
    catalogInfo: 'Informations sur le catalogue',
    // Portail mot de passe
    catalogTitle: 'CATALOGUE SONORE COMPLET',
    passwordPlaceholder: 'Mot de passe',
    passwordError: 'Mot de passe incorrect',
    enter: 'Entrer',
    privateAccess: 'Accès Privé',
    // Contenu du tooltip info
    infoLead: "L'intégralité de la production OTR Studio, réunie en un seul catalogue.",
    infoBody: "Chaque morceau est une création originale, disponible pour vos projets de synchro, publicité et audiovisuel.",
    infoNote: "Tous les titres restent disponibles. Aucun n'a encore été exploité commercialement.",
    infoContactBefore: 'Pour toute demande liée au catalogue, contactez-',
    infoContactLink: 'nous',
    infoContactAfter: '.',
  },

  en: {
    // Search & filters
    searchPlaceholder: 'Search a track…',
    allProjects: 'All projects',
    allStyles: 'All styles',
    mainProjects: 'Main Projects',
    backCatalog: 'Back Catalog',
    filterByProject: 'Filter by project',
    filterByStyle: 'Filter by style',
    results: (n) => `${n} result${n !== 1 ? 's' : ''}`,
    noResults: 'No tracks found',
    // Player
    prev: 'Previous',
    next: 'Next',
    pause: 'Pause',
    play: 'Play',
    volume: 'Volume',
    volumeControl: 'Volume control',
    // Selection / cart
    mySelection: 'My selection',
    likedTracksDialog: 'Liked tracks',
    emptyCart: 'Like tracks with the heart to add them here.',
    downloading: 'Downloading…',
    download: 'Download',
    downloadCount: (n) => `Download (${n})`,
    downloadError: 'Download error.',
    addToSelection: (name) => `Add ${name} to my selection`,
    removeFromSelection: (name) => `Remove ${name} from my selection`,
    removeTrack: (name) => `Remove ${name}`,
    // Theme & UI
    palette: 'Palette',
    colorPalettes: 'Color palettes',
    choosePalette: 'Choose a color palette',
    close: 'Close',
    // Info
    catalogInfo: 'Catalog information',
    // Password gate
    catalogTitle: 'FULL SOUND CATALOG',
    passwordPlaceholder: 'Password',
    passwordError: 'Wrong password',
    enter: 'Enter',
    privateAccess: 'Private Access',
    // Info tooltip content
    infoLead: 'The complete OTR Studio production in one catalog.',
    infoBody: 'Each track is an original composition, available for your sync, advertising, and audiovisual projects.',
    infoNote: 'All tracks are available. None have been commercially exploited yet.',
    infoContactBefore: 'For any catalog inquiry, contact ',
    infoContactLink: 'us',
    infoContactAfter: '.',
  },

  es: {
    // Búsqueda & filtros
    searchPlaceholder: 'Buscar una pista…',
    allProjects: 'Todos los proyectos',
    allStyles: 'Todos los estilos',
    mainProjects: 'Proyectos Principales',
    backCatalog: 'Catálogo',
    filterByProject: 'Filtrar por proyecto',
    filterByStyle: 'Filtrar por estilo',
    results: (n) => `${n} resultado${n !== 1 ? 's' : ''}`,
    noResults: 'No se encontraron pistas',
    // Reproductor
    prev: 'Anterior',
    next: 'Siguiente',
    pause: 'Pausa',
    play: 'Reproducir',
    volume: 'Volumen',
    volumeControl: 'Control de volumen',
    // Selección / carrito
    mySelection: 'Mi selección',
    likedTracksDialog: 'Pistas guardadas',
    emptyCart: 'Dale al corazón en una pista para añadirla aquí.',
    downloading: 'Descargando…',
    download: 'Descargar',
    downloadCount: (n) => `Descargar (${n})`,
    downloadError: 'Error de descarga.',
    addToSelection: (name) => `Añadir ${name} a mi selección`,
    removeFromSelection: (name) => `Quitar ${name} de mi selección`,
    removeTrack: (name) => `Quitar ${name}`,
    // Tema & UI
    palette: 'Paleta',
    colorPalettes: 'Paletas de colores',
    choosePalette: 'Elegir una paleta de colores',
    close: 'Cerrar',
    // Info
    catalogInfo: 'Información del catálogo',
    // Portada contraseña
    catalogTitle: 'CATÁLOGO SONORO COMPLETO',
    passwordPlaceholder: 'Contraseña',
    passwordError: 'Contraseña incorrecta',
    enter: 'Entrar',
    privateAccess: 'Acceso Privado',
    // Contenido del tooltip
    infoLead: 'La producción completa de OTR Studio en un solo catálogo.',
    infoBody: 'Cada pista es una composición original, disponible para tus proyectos de sincronización, publicidad y audiovisual.',
    infoNote: 'Todas las pistas están disponibles. Ninguna ha sido explotada comercialmente todavía.',
    infoContactBefore: 'Para cualquier consulta sobre el catálogo, contáctanos ',
    infoContactLink: 'aquí',
    infoContactAfter: '.',
  },
};

/**
 * Retourne l'objet de traductions correspondant à la langue du navigateur.
 * Calculé une seule fois au chargement du module.
 */
const _t = (() => {
  const lang = (
    (typeof navigator !== 'undefined' ? navigator.language : '') || 'en'
  ).split('-')[0].toLowerCase();
  return translations[lang] || translations.en;
})();

/** Hook React — retourne les chaînes UI pour la langue détectée. */
export function useLocale() {
  return _t;
}

/** Fonction utilitaire (hors composant) — même résultat que useLocale(). */
export function getLocale() {
  return _t;
}
